import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { BackgroundEffectsList, BackgroundEffectsPreview } from '..';
import { setBackgroundEffect } from '../../../actions';
import { defaultBackgroundEffects, getEffectsWithBrandedVirtualBackground, } from '../../../constants';
import { getBackgroundEffect } from '../../../selectors';
import { applyPublisherEffect, NoVideoSourceError } from '../helpers';
import './styles.scss';
import { selectInstitution, selectSubscriptionType, } from '../../../../../store/features/system';
import { AngularServices, getAngularService, } from '../../../../../modules/core';
export function BackgroundEffectsPanel() {
    const dispatch = useDispatch();
    const appliedEffect = useSelector(getBackgroundEffect);
    const videoProvider = getAngularService(AngularServices.VideoProviderFactory);
    const [effects, setEffects] = useState(defaultBackgroundEffects);
    const [selectedEffect, setSelectedEffect] = useState(appliedEffect);
    const [previousEffect, setPreviousEffect] = useState(appliedEffect);
    const [isApplyingEffect, setIsApplyingEffect] = useState(false);
    const publisher = videoProvider.activeProvider.getPublisher();
    const [previewVideo, setPreviewVideo] = useState();
    const [error, setError] = useState();
    const skipApplyEffect = useRef(false);
    const mounted = useRef(false);
    /**
     * Note: For providers with Clinic subscriptions accessing via regular login (Free/Pro),
     * clinic subscription features remain available but the interface defaults to standard view
     * rather than clinic-specific dashboard.
     */
    // Should return clinic data if the user is on the clinic subdomain
    const currentInstitution = useSelector(selectInstitution);
    // Check if the user has a clinic subscription type
    const hasClinicSubscription = useSelector(selectSubscriptionType) === 'clinic';
    const isClinic = hasClinicSubscription && Boolean(currentInstitution.id);
    const hasBrandedVirtualBackground = isClinic;
    const applyEffect = async () => {
        try {
            await applyPublisherEffect(selectedEffect, publisher);
            dispatch(setBackgroundEffect(selectedEffect));
        }
        catch (err) {
            console.error('Error applying video filter to call publisher', err);
        }
    };
    useEffect(() => {
        if (hasBrandedVirtualBackground) {
            const effectsWithBranded = getEffectsWithBrandedVirtualBackground();
            setEffects(effectsWithBranded);
        }
    }, [hasBrandedVirtualBackground, selectedEffect]);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    /**
     * Sets the preview video
     */
    useEffect(() => {
        void publisher?.videoElementPromise.then((element) => {
            if (publisher.getVideoSource().track?.enabled) {
                setPreviewVideo(element);
            }
            return element;
        });
    }, []);
    /**
     * Runs on `selectedEffect` change
     */
    useEffect(() => {
        async function tryApplyEffectToPreview() {
            if (isApplyingEffect) {
                return;
            }
            try {
                setIsApplyingEffect(true);
                setPreviousEffect(selectedEffect);
                await applyEffect();
                setError(undefined);
            }
            catch (err) {
                if (!mounted.current) {
                    return;
                }
                skipApplyEffect.current = true;
                if (err instanceof NoVideoSourceError) {
                    setPreviewVideo(null);
                }
                else {
                    setSelectedEffect(previousEffect);
                    setError(err);
                    console.error('Error applying video filter to preview publisher', err);
                }
            }
            finally {
                if (mounted.current) {
                    setIsApplyingEffect(false);
                }
            }
        }
        if (skipApplyEffect.current) {
            skipApplyEffect.current = false;
        }
        else if (publisher && selectedEffect !== previousEffect) {
            void tryApplyEffectToPreview();
        }
    }, [isApplyingEffect, previousEffect, selectedEffect, publisher]);
    /**
     * Automatically turns on the publisher video if it's off and the effect is changed
     */
    useEffect(() => {
        if (appliedEffect !== selectedEffect &&
            !videoProvider.activeProvider.getPublisher()?.getVideoSource().track
                ?.enabled) {
            void videoProvider.activeProvider.togglePublishersVideo(true).then(() => {
                const currentPublisher = videoProvider.activeProvider.getPublisher();
                if (currentPublisher?.getVideoSource().track?.enabled) {
                    setPreviewVideo(currentPublisher.videoElement());
                }
                return undefined;
            });
        }
    }, [appliedEffect, selectedEffect]);
    return (<div className="background-effects__panel">
      <BackgroundEffectsPreview video={previewVideo} isApplyingEffect={isApplyingEffect} effect={selectedEffect}/>

      {error && (<p className="background-effects__panel__error">
          <FormattedMessage id="backgroundEffects.errors.applyVideoFilter"/>
        </p>)}

      <BackgroundEffectsList effects={effects} selectedEffect={selectedEffect} onSelectEffect={setSelectedEffect}/>
    </div>);
}
